import type { ReactNode } from 'react';
import { useLingui } from '@lingui/react/macro';

import type { ActionMenuItem } from '@rover/components/src/callouts/ActionMenu';
import ActionMenu from '@rover/components/src/callouts/ActionMenu';
import PlayGlyph from '@rover/icons/svg/play-glyph.svg';
import { Box, Flex } from '@rover/kibble/core';
import DSTokenMap from '@rover/kibble/styles/DSTokenMap';

import {
  ACTIONS_TESTID,
  DIMMED_IMAGE_OPACITY,
  PLAY_BADGE_OPACITY,
  PLAY_BADGE_SIZE,
} from './constants';
import GallerySelectionIndicator from './GallerySelectionIndicator';
import type { GalleryItem } from './ImageGallery.common';
import { galleryItemKey, isGalleryVideoItem } from './ImageGallery.common';
import ImageWithShimmer from './ImageWithShimmer';

type GalleryTileProps = {
  image: GalleryItem;
  index: number;
  onImageClick: (index: number) => void;
  overlay?: ReactNode;
  actions?: ActionMenuItem[];
  onActionsOpenChange?: (isOpen: boolean) => void;
  isDimmed?: boolean;
  isManaging?: boolean;
  isSelectable?: boolean;
  isSelected?: boolean;
  onToggleSelected?: () => void;
};

const PLAY_BADGE_SIZE_PX = `${PLAY_BADGE_SIZE}px`;

const PLAY_BADGE_BACKGROUND = DSTokenMap.TEXT_COLOR_PRIMARY.withOpacity(PLAY_BADGE_OPACITY);
const PLAY_BADGE_FILL = `${DSTokenMap.TEXT_COLOR_PRIMARY_INVERSE}`;

const GalleryTile = ({
  image,
  index,
  onImageClick,
  overlay,
  actions,
  onActionsOpenChange,
  isDimmed = false,
  isManaging = false,
  isSelectable = false,
  isSelected = false,
  onToggleSelected,
}: GalleryTileProps): JSX.Element => {
  const { t } = useLingui();
  const isVideo = isGalleryVideoItem(image);
  const photoNumber = index + 1;
  // The number only stands in for a photo with no description.
  const photoName =
    (isVideo ? image.video.description : image.altText || image.caption) || t`photo ${photoNumber}`;
  const overlayId = `gallery-image-overlay-${galleryItemKey(image)}`;

  // Opening the lightbox would drop the mode the viewer is using.
  let onTilePress: (() => void) | undefined;
  if (isSelectable) {
    onTilePress = onToggleSelected;
  } else if (!isManaging) {
    onTilePress = () => onImageClick(index);
  }

  return (
    <>
      <Box sx={isDimmed ? { opacity: DIMMED_IMAGE_OPACITY } : undefined}>
        <ImageWithShimmer
          fluid
          src={image.small}
          caption={
            isVideo ? image.video.description : image.caption || t`View photo ${photoNumber}`
          }
          altText={isVideo ? undefined : image.altText}
          pressLabel={isSelectable ? t`Select ${photoName}` : undefined}
          selected={isSelectable ? isSelected : undefined}
          onClick={onTilePress}
          describedById={overlay ? overlayId : undefined}
        />
      </Box>
      {/* The grid shows the video's thumbnail with a play badge, never a
          player. Opening the tile is what starts playback, in the lightbox. */}
      {isVideo && (
        <Flex
          position="absolute"
          top="0"
          width="100%"
          height="100%"
          justifyContent="center"
          alignItems="center"
          data-testid="gallery-play-icon"
          // The badge sits over the tile's own click target, so it has to
          // let presses through rather than swallow them.
          sx={{ pointerEvents: 'none' }}
        >
          <Flex
            width={PLAY_BADGE_SIZE_PX}
            height={PLAY_BADGE_SIZE_PX}
            borderRadius="round"
            justifyContent="center"
            alignItems="center"
            sx={{ backgroundColor: PLAY_BADGE_BACKGROUND }}
          >
            <PlayGlyph
              height={PLAY_BADGE_SIZE_PX}
              width={PLAY_BADGE_SIZE_PX}
              fill={PLAY_BADGE_FILL}
            />
          </Flex>
        </Flex>
      )}
      {!!actions?.length && (
        <Box position="absolute" top="2x" right="2x" zIndex="1" data-testid={ACTIONS_TESTID}>
          <ActionMenu
            items={actions}
            triggerLabel={t`Photo ${photoNumber} actions`}
            onOpenChange={onActionsOpenChange}
          />
        </Box>
      )}
      {overlay && (
        <Flex
          id={overlayId}
          position="absolute"
          bottom="0"
          left="0"
          maxWidth="100%"
          padding="2x"
          data-testid="gallery-image-overlay"
          sx={{ pointerEvents: 'none' }}
        >
          {overlay}
        </Flex>
      )}
      {isSelectable && (
        <Flex position="absolute" bottom="0" right="0" padding="2x" sx={{ pointerEvents: 'none' }}>
          <GallerySelectionIndicator checked={isSelected} />
        </Flex>
      )}
    </>
  );
};

export default GalleryTile;
